import { useEffect, useState, type ReactNode } from 'react';
import type { Evidence } from '@sfpbxtestbot/contracts';
import { api } from '../lib/api.js';
import { EmptyState } from './primitives.js';

/**
 * Screenshot wall for a run.
 *
 * Every configuration step captures the Setup page before and after it acts, so
 * this is the audit trail: someone can scroll it and see exactly what the bot
 * saw, in order, without trusting a green tick.
 */
export function ScreenshotGallery({
  runId,
  evidence,
}: {
  runId: string;
  evidence: Evidence[];
}): ReactNode {
  const [openIndex, setOpenIndex] = useState<number>();
  const screenshots = evidence.filter((item) => item.kind === 'screenshot');

  useEffect(() => {
    if (openIndex === undefined) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpenIndex(undefined);
      if (event.key === 'ArrowRight') setOpenIndex((i) => Math.min(screenshots.length - 1, (i ?? 0) + 1));
      if (event.key === 'ArrowLeft') setOpenIndex((i) => Math.max(0, (i ?? 0) - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex, screenshots.length]);

  if (screenshots.length === 0) {
    return (
      <EmptyState
        title="No screenshots yet"
        body="Screenshots appear here as each step runs, showing the page before and after the bot acts on it."
      />
    );
  }

  const open = openIndex !== undefined ? screenshots[openIndex] : undefined;

  return (
    <>
      <div className="gallery">
        {screenshots.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className="gallery__item"
            onClick={() => setOpenIndex(index)}
          >
            <img
              className="gallery__image"
              src={api.artifactUrl(runId, item.id)}
              alt={item.caption}
              loading="lazy"
            />
            <span className="gallery__caption">{item.caption}</span>
          </button>
        ))}
      </div>

      {open ? (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={open.caption}
          onClick={() => setOpenIndex(undefined)}
        >
          <div onClick={(event) => event.stopPropagation()}>
            <img className="lightbox__image" src={api.artifactUrl(runId, open.id)} alt={open.caption} />
            <p className="lightbox__caption">
              {open.caption} · {openIndex! + 1} of {screenshots.length}
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
}

/** Renders a single piece of evidence in whatever form suits its kind. */
export function EvidenceItem({ runId, item }: { runId: string; item: Evidence }): ReactNode {
  if (item.kind === 'screenshot') {
    return (
      <a href={api.artifactUrl(runId, item.id)} target="_blank" rel="noreferrer">
        {item.caption}
      </a>
    );
  }
  if (item.kind === 'link' && item.url) {
    return (
      <a href={item.url} target="_blank" rel="noreferrer">
        {item.caption}
      </a>
    );
  }
  if (item.inline !== undefined) {
    return (
      <details className="disclosure">
        <summary>{item.caption}</summary>
        <pre className="code-block">{JSON.stringify(item.inline, null, 2)}</pre>
      </details>
    );
  }
  return (
    <a href={api.artifactUrl(runId, item.id)} target="_blank" rel="noreferrer">
      {item.caption}
    </a>
  );
}
